import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  collection,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import { db, OrgProRef, storage } from "../../../firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { getSingleUser } from "../../OrganizationQuickTales/hooks/QuickTalesHooks";
import moment from "moment";

export const getAllOrganization = async () => {
  try {
    const response = await getDocs(OrgProRef);
    console.log("refpass", response);
    return response;
  } catch (error) {
    console.log("Error fetching data from Firestore:", error.message);
    // console.error("Error fetching data from Firestore:", error.message);
    return [];
  }
};

export const getSingleOrg = async (id) => {
  try {
    return await getDoc(doc(db, "organizations", id));
  } catch (error) {
    console.log("error getting quicktale:", error.message);
  }
};

export const deleteOrganization = async (id) => {
  const OrgProRef = doc(db, "organizations", id);
  return await deleteDoc(OrgProRef);
};

export const addOrganization = async (data) => {
  const OrgProRef = collection(db, "organizations");
  return await addDoc(OrgProRef, data);
};

/**
 * Normalizes an org handle for storage: trims whitespace and strips a leading "@".
 * Casing is preserved for display; uniqueness checks are case-insensitive.
 */
export const normalizeHandle = (handle) =>
  String(handle ?? "").trim().replace(/^@+/, "").trim();

// Case variants used to query users.userName (Firestore equality is case-sensitive
// and users have no lowercase username field).
const buildCaseVariants = (value) => {
  const lower = value.toLowerCase();
  const variants = new Set([
    value,
    lower,
    value.toUpperCase(),
    lower.charAt(0).toUpperCase() + lower.slice(1),
  ]);
  return Array.from(variants).slice(0, 30);
};

/**
 * Checks that an org handle is unique across BOTH organization handles and
 * usernames (case-insensitive), because "created by" can resolve to either.
 *
 * @param {string} handle - Handle to validate.
 * @param {{ excludeOrgId?: string }} [options] - When editing, the org's own id.
 *   If the handle is unchanged (case-insensitive) from the stored value, it is
 *   treated as available so legacy data does not block unrelated edits.
 * @returns {Promise<{ available: boolean, reason: "empty"|"org"|"user"|null, message: string }>}
 */
export const checkOrgHandleAvailability = async (handle, options = {}) => {
  const { excludeOrgId } = options;
  const normalized = normalizeHandle(handle);
  if (!normalized) {
    return { available: false, reason: "empty", message: "Org handle is required." };
  }
  const target = normalized.toLowerCase();

  if (excludeOrgId) {
    const existing = await getDoc(doc(db, "organizations", excludeOrgId));
    const storedHandle = normalizeHandle(existing.exists() ? existing.data()?.handle : "");
    if (storedHandle && storedHandle.toLowerCase() === target) {
      return { available: true, reason: null, message: "" };
    }
  }

  const [orgSnap, userSnap] = await Promise.all([
    getDocs(collection(db, "organizations")),
    getDocs(
      query(collection(db, "users"), where("userName", "in", buildCaseVariants(normalized)))
    ),
  ]);

  const orgConflict = orgSnap.docs.some(
    (d) =>
      d.id !== excludeOrgId &&
      normalizeHandle(d.data()?.handle).toLowerCase() === target
  );
  if (orgConflict) {
    return {
      available: false,
      reason: "org",
      message: `The handle "${normalized}" is already used by another organization.`,
    };
  }

  const userConflict = userSnap.docs.some(
    (d) => normalizeHandle(d.data()?.userName).toLowerCase() === target
  );
  if (userConflict) {
    return {
      available: false,
      reason: "user",
      message: `The handle "${normalized}" is already used as a username.`,
    };
  }

  return { available: true, reason: null, message: "" };
};

export const addUser = async (data) => {
  const usersRef = collection(db, "users");
  return await addDoc(usersRef, data);
};

export const updateProData = async (prId, proData) => {
  const proDocRef = doc(db, "organization", prId);
  return await updateDoc(proDocRef, proData);
};

export const updateOrganization = async (orgId, docData) => {
  const orgDocRef = doc(db, "organizations", orgId);
  return await updateDoc(orgDocRef, docData);
};

export const uploadOrgImageToStorage = async (file) => {
  const newFilename = uuidv4();
  const storageRef = ref(storage, `organizations/images/${newFilename}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

export const organizationSearch = (array, keyword) => {
  const searchTerm = keyword.toLowerCase();
  return array.filter((value) => {
    return (
      (value.name &&
        value.name.toLowerCase().match(new RegExp(searchTerm, "g"))) ||
        (value.orgContact &&
          value.orgContact.toLowerCase().match(new RegExp(searchTerm, "g"))) ||
      (value.createdBy &&
        value.createdBy.toLowerCase().match(new RegExp(searchTerm, "g")))
    );
  });
};

export const getOrgUser = async (orgId) => {
  try {
    // const usersRef = collection(db, "users");
    // const q = query(usersRef, where("organization", "array-contains", orgId));
    const usersRef = collection(db, "users");
    // const q = query(usersRef, where("organization", "==", orgId));
    const q = query(usersRef, where("organization", "array-contains", orgId));

    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      // Assuming the orgId uniquely identifies a user, return the first match.
      const firstUserDoc = querySnapshot.docs[0];
      return { id: firstUserDoc.id, ...firstUserDoc.data() }; // Return a single user object
    } else {
      console.log("No matching user found");
      return null; // Return null if no user is found
    }
  } catch (error) {
    console.log("error getting user:", error.message);
    return null; // Return null in case of an error
  }
};

export const handleFilterChipClick = (
  index,
  options,
  setOptions,
  setFilterOptions
) => {
  let updatedOptions = [...options]; // Clone the current state to avoid direct mutation
  if (updatedOptions[index].label === "All") {
    // If "All" is selected, update all options to reflect the change
    console.log("all", updatedOptions);
    updatedOptions = updatedOptions.map((option, idx) => ({
      ...option,
      selected: idx === index ? true : false, // Select "All" and deselect others
    }));
    setFilterOptions([]); // Clear all filter options since "All" is selected
  } else {
    // For any option other than "All", update its selected state and ensure "All" is deselected
    updatedOptions[index].selected = !updatedOptions[index].selected;
    updatedOptions[0].selected = false; // Deselect "All"

    // Update the filter options based on the selected state
    const filterOptionsTemp = updatedOptions.reduce((acc, curr, idx) => {
      if (idx !== 0 && curr.selected) {
        // Skip "All" and include only selected options
        acc.push(curr.label);
      }
      return acc;
    }, []);

    setFilterOptions(filterOptionsTemp);
  }
  setOptions(updatedOptions); // Update the options state with the new changes
};

// export const getDateRange = async(period) {
//   const endDate = new Date();
//   let startDate = new Date();

//   switch (period) {
//     case "Today":
//       // Start and end dates are the same, already set to today
//       break;
//     case "Yesterday":
//       startDate.setDate(endDate.getDate() - 1);
//       endDate.setDate(endDate.getDate() - 1);
//       break;
//     case "Last 30 Days":
//       startDate.setDate(endDate.getDate() - 30);
//       break;
//     case "Last 12 Months":
//       startDate.setFullYear(endDate.getFullYear() - 1);
//       break;
//     default:
//       return null; // or throw an error
//   }

//   // Format dates as 'YYYY-MM-DD'
//   const format = (date) => date.toISOString().split("T")[0];

//   return {
//     startDate: format(startDate),
//     endDate: format(endDate),
//   };
// }

export const getDateRange = (creationDateFilterOptions, title) => {
  let endDate = new Date();
  let startDate = new Date();
  let period = null;

  if (Array.isArray(creationDateFilterOptions)) {
    const dateRangeOption = creationDateFilterOptions.find((option) =>
      option.startsWith(title ? `${title}:` : "Creation Date:")
    );
    if (dateRangeOption) {
      period = "Custom Range";
    } else {
      period = creationDateFilterOptions[0];
    }
  } else {
    period = creationDateFilterOptions;
  }

  switch (period) {
    case "Today":
      startDate = moment().startOf('day').toDate();
      endDate = moment().endOf('day').toDate();
      break;
    case "Yesterday":
      startDate = moment().subtract(1, 'days').startOf('day').toDate();
      endDate = moment().subtract(1, 'days').endOf('day').toDate();
      break;
    case "Past Week":
      startDate = moment().subtract(7, 'days').startOf('day').toDate();
      endDate = moment().endOf('day').toDate();
      break;
    case "Last 30 Days":
      startDate = moment().subtract(30, 'days').startOf('day').toDate();
      endDate = moment().endOf('day').toDate();
      break;
    case "Last 12 Months":
      startDate = moment().subtract(12, 'months').startOf('day').toDate();
      endDate = moment().endOf('day').toDate();
      break;
    case "Custom Range":
      const dateRangeOption = creationDateFilterOptions.find((option) =>
        option.startsWith(title ? `${title}:` : "Creation Date:")
      );
      const dates = dateRangeOption
        .replace(title ? `${title}: ` : "Creation Date: ", "")
        .split(" - ");
      startDate = moment(dates[0], "MM/DD/YYYY").startOf('day').toDate();
      endDate = moment(dates[1], "MM/DD/YYYY").endOf('day').toDate();
      break;
    default:
      startDate = moment().subtract(10, 'years').startOf('day').toDate();
      endDate = moment().add(10, 'years').endOf('day').toDate();
  }

  return {
    startDate: startDate,
    endDate: endDate,
  };
};

// export const getDateRange = (creationDateFilterOptions, title) => {
//   let endDate = new Date();
//   let startDate = new Date();
//   let period = null;
//   if (Array.isArray(creationDateFilterOptions)) {
//     // Example of finding a specific option, adjust logic as necessary
//     const dateRangeOption = creationDateFilterOptions.find((option) =>
//       option.startsWith(title ? `${title}:` : "Creation Date:")
//     );
//     if (dateRangeOption) {
//       period = "Custom Range";
//     } else {
//       period = creationDateFilterOptions[0];
//     }
//   } else {
//     period = creationDateFilterOptions;
//   }

//   switch (period) {
//     case "Today":
//       startDate = moment(new Date(), "MM/DD/YYYY").startOf("day").toDate();
//       endDate = moment(new Date(), "MM/DD/YYYY").endOf("day").toDate();
//       break;
//     case "Yesterday":
//       startDate = moment(endDate.getDate() - 1, "MM/DD/YYYY").startOf("day").toDate();
//       endDate = moment(endDate.getDate() - 1, "MM/DD/YYYY").endOf("day").toDate();
//       // startDate.setDate(endDate.getDate() - 1);
//       // endDate.setDate(endDate.getDate() - 1);
//       break;
//     case "Past Week":
//       // startDate.setDate(endDate.getDate() - 7);
//       startDate = moment(endDate.getDate() - 7, "MM/DD/YYYY").startOf("day").toDate();
//       endDate = moment(new Date(), "MM/DD/YYYY").endOf("day").toDate();
//       break;
//     case "Last 30 Days":
//       // startDate.setDate(endDate.getDate() - 30);
//       startDate = moment(endDate.getDate() - 30, "MM/DD/YYYY").startOf("day").toDate();
//       endDate = moment(new Date(), "MM/DD/YYYY").endOf("day").toDate();
//       break;
//     case "Last 12 Months":
//       // startDate.setFullYear(endDate.getFullYear() - 1);
//       startDate = moment(endDate.getFullYear() - 1, "MM/DD/YYYY").startOf("day").toDate();
//       // endDate = moment(new Date(), "MM/DD/YYYY").endOf("day").toDate();
//       break;
//     case "Custom Range":
//       const dateRangeOption = creationDateFilterOptions.find((option) =>
//         option.startsWith(title ? `${title}:` : "Creation Date:")
//       );
//       const dates = dateRangeOption
//         .replace(title ? `${title}: ` : "Creation Date: ", "")
//         .split(" - ");
//       startDate = moment(dates[0], "MM/DD/YYYY").startOf("day").toDate();
//       endDate = moment(dates[1], "MM/DD/YYYY").endOf("day").toDate();
//       break;
//     default:
//       startDate.setFullYear(endDate.getFullYear() - 10);
//       endDate.setFullYear(endDate.getFullYear() + 10);
//   }

//   // Format dates as 'YYYY-MM-DD'
//   // const format = (date) => date.toISOString().split("T")[0];
//   return {
//     startDate: startDate,
//     endDate: endDate,
//   };
// };
export const getFilterOrganizations = async (
  dateRange,
  statusFilterOptions,
  setLoading,
  handleFilterModalClose
) => {
  setLoading(true);

  // Create a function to deduplicate documents based on their IDs
  const deduplicate = (arr) => {
    const uniqueIds = new Set();
    const uniqueDocs = [];
    console.log("arr:", arr);
    arr.forEach((doc) => {
      if (!uniqueIds.has(doc.id)) {
        uniqueIds.add(doc.id);
        uniqueDocs.push(doc);
      }
    });
    console.log("results:", uniqueDocs);
    handleFilterModalClose();
    return uniqueDocs;
  };

  try {
    let results = [];

    const statusOptions =
      statusFilterOptions.length > 0 ? statusFilterOptions : [null];

    for (const status of statusOptions) {
      let queries = [];

      if (status !== null) {
        queries.push(where("status", "==", status.toLowerCase()));
      }

      if (dateRange && dateRange.startDate && dateRange.endDate) {
        queries.push(where("createdAt", ">=", dateRange.startDate));
        queries.push(where("createdAt", "<=", dateRange.endDate));
      }

      if (queries.length > 0) {
        const q = query(OrgProRef, ...queries);
        const querySnapshot = await getDocs(q);

        const fetchDetailsPromises = querySnapshot.docs.map(async (doc) => {
          const userData = await getOrgUser(doc.id);

          return {
            id: doc.id,
            ...doc.data(),
            userData: userData,
          };
        });

        // Wait for all enrichment promises to resolve
        const queryResults = await Promise.all(fetchDetailsPromises);
        results = results.concat(queryResults);
      }
    }

    // Deduplicate the results
    const uniqueResults = deduplicate(results);
    console.log("uniqueResults", uniqueResults);
    handleFilterModalClose();
    return uniqueResults;
  } catch (error) {
    console.error("Error filtering organizations", error);
    return []; // Return an empty array in case of error
  } finally {
    setLoading(false);
  }
};

// export const getFilterOrganizations = async (
//   // whoCanListenFilterOptions,
//   statusFilterOptions,
//   dateRange,
//   setLoading,
//   handleFilterModalClose,
//   filterOptions
// ) => {
//   // Create a function to deduplicate documents based on their IDs
//   const deduplicate = (arr) => {
//     const uniqueIds = new Set();
//     const uniqueDocs = [];
//     console.log("arr:", arr);
//     arr.forEach((doc) => {
//       if (!uniqueIds.has(doc.id)) {
//         uniqueIds.add(doc.id);
//         uniqueDocs.push(doc);
//       }
//     });
//     console.log("results:", uniqueDocs);
//     handleFilterModalClose();
//     return uniqueDocs;
//   };

//   try {
//     // Perform queries for 'ORG Status' options
//     const statusPromises = statusFilterOptions.map(
//       async (option) =>
//         await getDocs(query(OrgProRef, where("status", "==", option)))
//     );
//     // Wait for all queries to complete and combine the results
//     const allResults = await Promise.all([...statusPromises]);

//     // Use map to transform querySnapshots into promises of user data enrichment
//     console.log("allResults", statusFilterOptions);
//     const enrichmentPromises = allResults.flatMap((querySnapshot) =>
//       querySnapshot.docs.map(async (doc) => {
//         console.log("results::::", doc.data());

//         const userTempPromise = getSingleUser(doc?.data()?.createdBy);

//         // Wait for both user data promises to resolve
//         const [userTemp] = await Promise.all([userTempPromise]);

//         return {
//           id: doc.id,
//           ...doc.data(),
//           userData: userTemp?.data() ? userTemp?.data() : {},
//           // quicktaleData: [],
//         };
//       })
//     );

//     // Wait for all enrichment promises to resolve
//     const enrichedResults = await Promise.all(enrichmentPromises);

//     // Deduplicate the results
//     console.log("Enriched Results:", enrichedResults);
//     const deduplicatedResults = deduplicate(enrichedResults);

//     console.log("Deduplicated Results:", deduplicatedResults);
//     handleFilterModalClose();
//     return deduplicatedResults;
//   } catch (error) {
//     console.error("Error filtering Organizations:", error);
//     return []; // Return an empty array in case of error
//   } finally {
//     // setLoading(false);
//   }
// };
